// Hover-to-expand controller for the desktop sidebar.
// Per doctrine answer A3: 150–300ms open delay so brief mouse passes don't expand the rail.
// Close is faster to feel responsive. When `pinned`, stay expanded and ignore leave/blur.

import { useCallback, useEffect, useRef, useState } from "react";

export type UseHoverExpandArgs = {
  /** Disable hover behavior entirely (e.g. tablet / mobile variants). */
  disabled?: boolean;
  /** Keep expanded (e.g. user preference "Keep sidebar open"). */
  pinned?: boolean;
  /** Open delay in ms. Defaults to 200ms (mid-range of 150–300). */
  openDelayMs?: number;
  /** Close delay in ms. Defaults to 80ms. */
  closeDelayMs?: number;
};

export type UseHoverExpandResult = {
  expanded: boolean;
  /** Force-open (e.g. on focus-within or pinned). */
  pin: () => void;
  /** Force-close immediately, cancelling any pending open. */
  unpin: () => void;
  /** Attach to the root element of the rail. */
  bind: {
    onMouseEnter: () => void;
    onMouseLeave: () => void;
    onFocus: () => void;
    onBlur: () => void;
  };
};

export function useHoverExpand({
  disabled = false,
  pinned = false,
  openDelayMs = 200,
  closeDelayMs = 80,
}: UseHoverExpandArgs = {}): UseHoverExpandResult {
  const [rawExpanded, setExpanded] = useState(false);
  const openTimerRef = useRef<number | null>(null);
  const closeTimerRef = useRef<number | null>(null);

  // Derived: disabled → collapsed; pinned → always open; else hover state.
  const expanded = disabled ? false : pinned ? true : rawExpanded;

  const clearTimers = useCallback(() => {
    if (openTimerRef.current != null) {
      window.clearTimeout(openTimerRef.current);
      openTimerRef.current = null;
    }
    if (closeTimerRef.current != null) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const scheduleOpen = useCallback(() => {
    if (disabled || pinned) return;
    clearTimers();
    openTimerRef.current = window.setTimeout(() => setExpanded(true), openDelayMs);
  }, [disabled, pinned, openDelayMs, clearTimers]);

  const scheduleClose = useCallback(() => {
    if (disabled || pinned) return;
    clearTimers();
    closeTimerRef.current = window.setTimeout(() => setExpanded(false), closeDelayMs);
  }, [disabled, pinned, closeDelayMs, clearTimers]);

  const pin = useCallback(() => {
    clearTimers();
    setExpanded(true);
  }, [clearTimers]);

  const unpin = useCallback(() => {
    clearTimers();
    setExpanded(false);
  }, [clearTimers]);

  useEffect(() => {
    if (disabled) clearTimers();
  }, [disabled, clearTimers]);

  useEffect(() => {
    if (pinned) {
      clearTimers();
      setExpanded(true);
    }
  }, [pinned, clearTimers]);

  useEffect(() => clearTimers, [clearTimers]);

  return {
    expanded,
    pin,
    unpin,
    bind: {
      onMouseEnter: scheduleOpen,
      onMouseLeave: scheduleClose,
      onFocus: scheduleOpen,
      onBlur: scheduleClose,
    },
  };
}
